import { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LABEL = {
  proveedores: "Proveedores", clientes: "Clientes",
  "materias-primas": "Materias primas", productos: "Productos",
};

export default function ImportDialog({ entidad, open, onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setLoading(false); };
  const cerrar = () => { reset(); onClose(); };

  const bajarPlantilla = async () => {
    try {
      const { data } = await api.get(`/importaciones/plantilla/${entidad}`);
      window.open(data.url, "_blank");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const analizar = async () => {
    if (!file) return toast.error("Selecciona un fichero .xlsx o .csv");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/importaciones/preview/${entidad}`, fd);
      setPreview(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const bajarErrores = async () => {
    try {
      const { data } = await api.get(`/importaciones/errores/${preview.preview_id}`);
      window.open(data.url, "_blank");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const confirmar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/importaciones/confirmar/${preview.preview_id}`);
      toast.success(`Importación completada: ${data.creadas} nuevas, ${data.actualizadas} actualizadas`);
      cerrar(); onDone && onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const r = preview?.resumen;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47] flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-[#FF5625]" />Importar {LABEL[entidad]}
        </DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <p className="text-sm text-[#0E2A47]/70">Descarga la plantilla, rellénala y súbela. Verás una vista previa antes de aplicar nada.</p>
            <Button variant="outline" onClick={bajarPlantilla} data-testid="import-plantilla"><Download size={15} className="mr-1" />Descargar plantilla .xlsx</Button>
            <div className="rounded-md border-2 border-dashed border-[#E2E8F0] p-6 text-center">
              <input type="file" accept=".xlsx,.csv" hidden ref={fileRef} onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
              <Button variant="ghost" onClick={() => fileRef.current?.click()} data-testid="import-seleccionar"><Upload size={15} className="mr-1" />{file ? file.name : "Seleccionar fichero"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="import-resumen">
              <Badge className="bg-[#10B981] hover:bg-[#10B981]">{r.nuevas} nuevas</Badge>
              <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47]">{r.actualizadas} actualizadas</Badge>
              <Badge className={r.errores ? "bg-[#EF4444] hover:bg-[#EF4444]" : "bg-[#94A3B8] hover:bg-[#94A3B8]"}>{r.errores} errores</Badge>
              <Badge variant="outline">{r.total} filas</Badge>
            </div>
            <div className="max-h-64 overflow-auto rounded-md border border-[#E2E8F0]">
              <table className="w-full text-xs" data-testid="import-preview-tabla">
                <thead className="bg-[#F7F8FA] sticky top-0"><tr>
                  <th className="text-left p-2">Fila</th><th className="text-left p-2">Nombre</th>
                  <th className="text-left p-2">Acción</th><th className="text-left p-2">Motivo</th>
                </tr></thead>
                <tbody>
                  {preview.filas.map((f) => (
                    <tr key={f.fila} className="border-t border-[#F1F5F9]">
                      <td className="p-2">{f.fila}</td>
                      <td className="p-2">{f.datos?.nombre || "—"}</td>
                      <td className="p-2">
                        {f.accion === "error"
                          ? <span className="text-[#EF4444] font-semibold">error</span>
                          : <span className={f.accion === "nueva" ? "text-[#10B981]" : "text-[#0E2A47]"}>{f.accion}</span>}
                      </td>
                      <td className="p-2 text-[#EF4444]">{f.motivo || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {r.errores > 0 && (
              <div className="flex items-center gap-2 text-xs text-[#B45309] bg-[#FEF3C7] rounded-md p-2">
                <AlertTriangle size={14} /> Las filas con error se omitirán. Puedes descargarlas, corregirlas y volver a importar.
                <Button size="sm" variant="ghost" onClick={bajarErrores} data-testid="import-errores" className="ml-auto h-7 text-[#B45309]"><Download size={13} className="mr-1" />Errores</Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!preview ? (
            <Button onClick={analizar} disabled={loading || !file} data-testid="import-analizar" className="bg-[#0E2A47] hover:bg-[#163C65]">{loading ? "Analizando…" : "Analizar"}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={reset} data-testid="import-otro"><RefreshCw size={14} className="mr-1" />Otro fichero</Button>
              <Button onClick={confirmar} disabled={loading || (r.nuevas + r.actualizadas === 0)} data-testid="import-confirmar" className="bg-[#10B981] hover:bg-[#0f9d74]"><CheckCircle2 size={15} className="mr-1" />Confirmar ({r.nuevas + r.actualizadas})</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
